import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "@/components/common/Button";
import { CheckOutlined } from "@/config/antd";
import {
  fetchOrganizationById,
  setCurrentStep,
} from "@/store/redux/slices/kyc/kyc_setup_slice";
import { calculateKycStatus } from "@/utils/kyc_utils";
import { useLoader } from "@/hooks/LoaderProvider";

const ManageKYCContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const { organizationData } = useSelector((state) => state.organization);
  const [kycStatus, setKycStatus] = useState({
    companyInfo: { complete: false },
    ownersInfo: { complete: false },
    businessInfo: { complete: false },
    isApproved: false,
  });

  useEffect(() => {
    let cancelled = false;

    // Skip if SettingsMenu (or another screen) already hydrated org data.
    // Omit showLoader/hideLoader from deps so loader identity changes don't re-fire.
    const fetchOrganization = async () => {
      if (Array.isArray(organizationData) && organizationData.length > 0) {
        return;
      }

      showLoader();
      try {
        await dispatch(fetchOrganizationById()).unwrap();
      } catch (error) {
        if (error?.name === "ConditionError") return;
        console.error("Error fetching organization:", error);
      } finally {
        if (!cancelled) {
          hideLoader();
        }
      }
    };

    fetchOrganization();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid loader identity remount spam
  }, [dispatch, organizationData]);

  useEffect(() => {
    const status = calculateKycStatus(organizationData);
    setKycStatus({
      companyInfo: { complete: status.steps.companyInfo },
      ownersInfo: { complete: status.steps.ownersInfo },
      businessInfo: { complete: status.steps.businessInfo },
      isApproved: status.isApproved,
    });
  }, [organizationData]);

  const handleStepNavigation = (stepNumber) => {
    dispatch(setCurrentStep(stepNumber));
    switch (stepNumber) {
      case 1:
        navigate("/account-setup-step-1");
        break;
      case 2:
        navigate("/account-setup-step-2");
        break;
      case 3:
        navigate("/account-setup-step-3");
        break;
      default:
        break;
    }
  };

  return (
    <div className="mt-4">
      {/* Account Verified Section - Show only if approved */}
      {kycStatus.isApproved && (
        <div className="p-4 mb-6 rounded-lg bg-secondary">
          <h2 className="mb-1 text-bold">Account Verified</h2>
          <p className="text-regular text-lighterGrey">
            There&apos;s nothing to do here. Unless you want to modify your
            already verified data. If so, you&apos;ll have to go through the
            entire approval process again.
          </p>
        </div>
      )}

      {/* Incomplete Section */}
      {(!kycStatus.companyInfo.complete ||
        !kycStatus.ownersInfo.complete ||
        !kycStatus.businessInfo.complete) && (
        <div className="mb-6">
          <h3 className="mb-2 text-bold">Incomplete Section</h3>

          {!kycStatus.companyInfo.complete && (
            <div className="flex items-center justify-between pt-4">
              <span className="text-regular">Company Information</span>
              <Button
                text="Click to Complete"
                customBgColor="secondary"
                customTextColor="primary"
                className="text-small-xs w-[105px] h-[15px] whitespace-nowrap"
                rounded="full"
                onClick={() => handleStepNavigation(1)}
              />
            </div>
          )}

          {!kycStatus.ownersInfo.complete && (
            <div className="flex items-center justify-between pt-4">
              <span className="text-regular">Owners&apos; Information</span>
              <Button
                text="Click to Complete"
                customBgColor="secondary"
                customTextColor="primary"
                className="text-small-xs w-[105px] h-[15px] whitespace-nowrap"
                rounded="full"
                onClick={() => handleStepNavigation(2)}
              />
            </div>
          )}

          {!kycStatus.businessInfo.complete && (
            <div className="flex items-center justify-between pt-4">
              <span className="text-regular">Business Information</span>
              <Button
                text="Click to Complete"
                customBgColor="secondary"
                customTextColor="primary"
                className="text-small-xs w-[105px] h-[15px] whitespace-nowrap"
                rounded="full"
                onClick={() => handleStepNavigation(3)}
              />
            </div>
          )}
        </div>
      )}

      {/* Complete Section */}
      <div>
        <h3 className="mb-2 text-bold">Complete Section</h3>
        <div className="space-y-4">
          {kycStatus.companyInfo.complete && (
            <div className="flex items-center justify-between pt-4">
              <span className="text-regular">Company Information</span>
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-secondary">
                <CheckOutlined className="text-primary" />
              </div>
            </div>
          )}

          {kycStatus.ownersInfo.complete && (
            <div className="flex items-center justify-between pt-4">
              <span className="text-regular">Owners&apos; Information</span>
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-secondary">
                <CheckOutlined className="text-primary" />
              </div>
            </div>
          )}

          {kycStatus.businessInfo.complete && (
            <div className="flex items-center justify-between pt-4">
              <span className="text-regular">Business Information</span>
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-secondary">
                <CheckOutlined className="text-primary" />
              </div>
            </div>
          )}

          {/* Show message if no sections are complete */}
          {!kycStatus.companyInfo.complete &&
            !kycStatus.ownersInfo.complete &&
            !kycStatus.businessInfo.complete && (
              <p className="text-regular text-lighterGrey pt-4">
                No sections completed yet.
              </p>
            )}
        </div>
      </div>

      <Button
        text="UPDATE INFO"
        className="w-full mt-6 lg:hidden"
        customBgColor="primary"
        customTextColor="white"
        onClick={() => handleStepNavigation(1)}
      />
    </div>
  );
};

export default ManageKYCContent;
